import i18next from 'i18next';
import I18NextHttpBackend from 'i18next-http-backend';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { APP_VERSION } from '../version';

interface Translation {
  [key: string]: undefined | string | Translation;
}

const TRANSLATION_ASSET_VERSION_KEY = 'translationAssetVersion';

const getTranslationAssetVersion = (): string => {
  if (typeof window === 'undefined') return APP_VERSION;

  return localStorage.getItem(TRANSLATION_ASSET_VERSION_KEY) || APP_VERSION;
};

const localeAliases: Record<string, string> = {
  bg: 'bg-BG',
  cs: 'cz-CZ',
  cz: 'cz-CZ',
  da: 'da-DA',
  fi: 'fi-FI',
  hr: 'hr-HR',
  hu: 'hu-HU',
  ja: 'ja-JA',
  no: 'no-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sk: 'sk-SK',
  sr: 'sr-RS',
  sv: 'sv-SV',
  tr: 'tr-TR',
  ua: 'ua-UA',
  uk: 'ua-UA',
};

export const setTranslationAssetVersion = (version: string): void => {
  if (typeof window === 'undefined') return;

  localStorage.setItem(TRANSLATION_ASSET_VERSION_KEY, version);
};

export const reloadTranslationResources = async (
  version?: string,
): Promise<void> => {
  if (version) {
    setTranslationAssetVersion(version);
  }

  await i18next.reloadResources();
  await i18next.changeLanguage(i18next.resolvedLanguage || i18next.language);
};

i18next
  .use(I18NextHttpBackend)
  .use(I18nextBrowserLanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'de',
    react: {
      useSuspense: true,
    },
    backend: {
      loadPath: (languages: readonly string[], namespaces: readonly string[]) =>
        `/assets/locales/${localeAliases[languages[0]] ?? languages[0]}/${namespaces[0]}.json?v=${encodeURIComponent(
          getTranslationAssetVersion(),
        )}`,
    },
    returnedObjectHandler(key: string, value: unknown, options) {
      const obj = value as Translation;
      const self = obj['self'];
      if (self && typeof self === 'string') {
        return i18next.t(`${key}.self`, options);
      }
      return JSON.stringify(obj);
    },
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  });

export default i18next;
